import { motion } from "framer-motion";
import type { MotionValue } from "framer-motion";
import type * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Isometric "engine" layers rebuilt in pure HTML/CSS (plan:
 * .context/underwriting-engine-css-rebuild-plan.md, from Figma group
 * 7617-32831). The previous SVG-image + clip-path + backdrop-filter build is
 * gone — WebKit silently disables backdrop-filter when clip-path sits on the
 * same element, which killed the glass frosting in Safari.
 *
 * Shared shape: each diamond is a centred square (70.71% of the box width) with
 * `transform: scaleY(0.5) rotate(-45deg)` — CSS applies right→left, so the
 * square rotates into an axis-aligned diamond FIRST and squashes to 2:1 after —
 * plus border-radius and overflow:hidden for the rounded-corner clip. The
 * left/right apexes land exactly on the box edges, keeping the dashed guide
 * lines in the visual aligned.
 *
 * Backdrop-root guard (load-bearing): never add `filter`, `opacity<1`,
 * `isolation`, `mask`, `contain`/`container-type` or `mix-blend-mode` to the
 * glass shell, the glass outer box, the motion wrappers, the stack container
 * or EnginePanel — any of them becomes a backdrop root between the frost and
 * the colour cards and the frosting silently disappears again.
 */

const DIAMOND_SHELL: React.CSSProperties = {
  width: "70.71%",
  aspectRatio: "1",
  transform: "scaleY(0.5) rotate(-45deg)",
  transformOrigin: "center",
  borderRadius: "4%",
  overflow: "hidden",
};

/** Figma text projection — labels/wordmark sit as siblings OVER the face; the
 * diamond's own transform is a different projection they must not inherit. */
const TEXT_PROJECTION = "rotate(-25deg) skewX(39deg) scaleY(0.78)";

/* ------------------------------------------------------------------ */
/* Colour cards                                                        */
/* ------------------------------------------------------------------ */

export type EngineCardVariant = "orange" | "magenta" | "blue";

const GLOW: Record<EngineCardVariant, string> = {
  orange: "rgba(255, 96, 64, 0.4)",
  magenta: "rgba(201, 74, 214, 0.36)",
  blue: "rgba(0, 120, 255, 0.4)",
};

// Face gradients in SQUARE (pre-transform) space. Angles AND stop positions
// are the Figma gradients' exact segments mapped through the shell transform —
// Figma's endpoints extend beyond the card (orange's end sits 235px above it),
// so stops must keep their out-of-range positions instead of being squeezed
// into 0–100%; that segment cropping is what makes orange read salmon→pink
// rather than the full ramp. 4-stop reductions of the 25-stop ramps.
const FACE: Record<EngineCardVariant, string> = {
  orange:
    "linear-gradient(17.2deg, #FE634D 21.1%, #E040B4 79.4%, #942AEF 169.3%, #001EFF 256.8%)",
  magenta:
    "linear-gradient(307.7deg, #FE634D -3.1%, #E040B4 24.1%, #942AEF 65.9%, #001EFF 106.6%)",
  blue: "linear-gradient(233.2deg, #001EFF -23.3%, #0077F7 30.9%, #00A7F9 78.3%, #00DDFE 139.4%)",
};

// Blue-only sheen — intra-card overlay blend is legal (both operands live in
// the card's own stacking context).
const BLUE_SHEEN = "linear-gradient(332.1deg, #00DDFE, #001EFF)";

const LABEL: Record<EngineCardVariant, string> = {
  orange: "Performance\ndata in",
  magenta: "Underwriting\ndecision made",
  blue: "Capital out",
};

const LABEL_TRANSFORM: Record<EngineCardVariant, string> = {
  // Figma places the orange label's bbox upper-middle.
  orange: `translateY(-8%) ${TEXT_PROJECTION}`,
  magenta: TEXT_PROJECTION,
  blue: TEXT_PROJECTION,
};

interface UnderwritingEngineCardProps {
  variant: EngineCardVariant;
  className?: string;
}

export function UnderwritingEngineCard({
  variant,
  className,
}: UnderwritingEngineCardProps) {
  return (
    <span
      aria-hidden="true"
      className={cn("relative block aspect-[337/174] w-full", className)}
      style={{
        // cqw label sizing — safe here: no backdrop-filter descendant.
        containerType: "inline-size",
        filter: `drop-shadow(0 16px 30px ${GLOW[variant]})`,
      }}
    >
      {/* Slab face */}
      <span
        className="absolute inset-0 m-auto"
        style={{
          ...DIAMOND_SHELL,
          background: FACE[variant],
          border: "1px solid rgba(255,255,255,0.16)",
          // Figma stacks TWO white inner shadows (blur σ31 ≈ 62px CSS) in
          // overlay blend — the luminous edge fringe that makes the slab
          // read thick/bevelled.
          boxShadow:
            "inset 0 0 62px rgba(255,255,255,0.45), inset 0 0 62px rgba(255,255,255,0.3)",
        }}
      >
        {variant === "blue" && (
          <span
            className="absolute inset-0"
            style={{ background: BLUE_SHEEN, mixBlendMode: "overlay" }}
          />
        )}
        {/* Front rim — the square's left+bottom borders render as the
            diamond's two lower/front edges. */}
        <span
          className="absolute inset-0"
          style={{
            borderRadius: "inherit",
            borderLeft: "1.5px solid rgba(255,255,255,0.5)",
            borderBottom: "1.5px solid rgba(255,255,255,0.5)",
          }}
        />
      </span>

      {/* Label — sibling over the face, Figma text projection. */}
      <span className="absolute inset-0 grid place-items-center">
        <span
          className="text-center whitespace-pre-line"
          style={{
            transform: LABEL_TRANSFORM[variant],
            transformOrigin: "center",
            color: "#fff",
            mixBlendMode: "lighten",
            fontFamily: "var(--font-inter, Inter), sans-serif",
            fontSize: "4.15cqw",
            lineHeight: 1.15,
            letterSpacing: "-0.01em",
            fontFeatureSettings: '"ss03","ss01","zero","ss02"',
          }}
        >
          {LABEL[variant]}
        </span>
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Glass sheets                                                        */
/* ------------------------------------------------------------------ */

export type EngineGlassVariant = "mid" | "top";

// Frost chain. blur 12 (not Figma's literal 25 — that washes the sampled
// backdrop to a flat tint and costs more paint); saturate restores the colour
// punch Figma got from an overlay blend that cannot cross motion wrappers.
const BACKDROP = "blur(12px) saturate(1.8) brightness(1.04) contrast(1.05)";

// Glass depth. 225deg in square space renders screen top→bottom under the
// shell transform. Moderate alpha — heavier muddies the colour reflection.
const DARK_RAMP =
  "linear-gradient(225deg, rgba(0,0,0,0.18) 0%, rgba(4,9,20,0.20) 28%, rgba(0,0,0,0.18) 100%)";

interface UnderwritingEngineGlassProps {
  variant: EngineGlassVariant;
  /**
   * Reflection of the neighbouring layers' colours — a guaranteed-visible
   * floor under the backdrop-filter sampling (45deg in square space renders
   * screen bottom→top).
   */
  tint?: string;
  className?: string;
}

export function UnderwritingEngineGlass({
  variant,
  tint,
  className,
}: UnderwritingEngineGlassProps) {
  return (
    <span
      aria-hidden="true"
      className={cn("relative block aspect-[337/174] w-full", className)}
    >
      {/* Shell = clipper only: geometry + perimeter hairline. No filters.
          borderRadius MUST stay 0 on THIS element: a rotated element with
          overflow:hidden AND border-radius forces Chromium onto a mask-based
          clip, which is a backdrop root — the frost inside would sample an
          empty backdrop (the invisible-blur bug). The rounding lives on the
          CHILDREN instead (frost/tint/rims carry their own border-radius),
          which Chromium samples fine, so the glass matches the cards'
          rounded corners without re-breaking the frost. */}
      <span
        className="absolute inset-0 m-auto"
        style={{
          ...DIAMOND_SHELL,
          borderRadius: 0,
        }}
      >
        {/* Frost — backdrop-filter ONLY, radius on itself (not the shell). */}
        <span
          className="absolute inset-0"
          style={{
            borderRadius: "4%",
            backdropFilter: BACKDROP,
            WebkitBackdropFilter: BACKDROP,
          }}
        />
        {/* Neighbour-colour reflection floor + glass-depth dark ramp. */}
        <span
          className="absolute inset-0"
          style={{
            borderRadius: "4%",
            border: "1px solid rgba(255,255,255,0.22)",
            background: tint ? `${tint}, ${DARK_RAMP}` : DARK_RAMP,
          }}
        />
        {/* Front rim (two lower edges). */}
        <span
          className="absolute inset-0"
          style={{
            borderRadius: "4%",
            borderLeft: "1.5px solid rgba(255,255,255,0.5)",
            borderBottom: "1.5px solid rgba(255,255,255,0.5)",
          }}
        />
        {/* Top sheet: back rim — the two upper edges peek above the stack. */}
        {variant === "top" && (
          <span
            className="absolute inset-0"
            style={{
              borderRadius: "4%",
              borderTop: "1px solid rgba(255,255,255,0.4)",
              borderRight: "1px solid rgba(255,255,255,0.4)",
            }}
          />
        )}
      </span>

      {/* Wordmark — top sheet only, Figma text projection over the frost. */}
      {variant === "top" && (
        <span
          className="absolute top-1/2 left-1/2"
          style={{
            width: "23%",
            transform: `translate(-50%,-50%) ${TEXT_PROJECTION}`,
            transformOrigin: "center",
          }}
        >
          <EngineWordmark />
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* The glass box — ONE rounded hexagon (Figma 7617:32860)              */
/* ------------------------------------------------------------------ */

// Rounded hexagon in a 340×286 canvas (lid top vertex → floor front vertex),
// corners rounded 12px along each edge. One clip shapes the whole cube.
const HEX_CLIP =
  'path("M 0 97 Q 0 85 10.7 79.6 L 159.3 5.4 Q 170 0 180.7 5.4 L 329.3 79.6 Q 340 85 340 97 L 340 189 Q 340 201 329.3 206.4 L 180.7 280.6 Q 170 286 159.3 280.6 L 10.7 206.4 Q 0 201 0 189 Z")';

const HEX_OUTLINE =
  "M 0 97 Q 0 85 10.7 79.6 L 159.3 5.4 Q 170 0 180.7 5.4 L 329.3 79.6 Q 340 85 340 97 L 340 189 Q 340 201 329.3 206.4 L 180.7 280.6 Q 170 286 159.3 280.6 L 10.7 206.4 Q 0 201 0 189 Z";

interface UnderwritingEngineBoxProps {
  /** 0→1 fade of the whole box (motion value or static). */
  veilOpacity?: MotionValue<number> | number;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * The closed glass cube, as ONE shape: a rounded-hexagon clip whose interior
 * is painted (faces sampled from the Figma cube render, 7617:32860) and
 * melted together with a regular `filter: blur` — no backdrop-filter
 * anywhere, so the clip is safe in every browser, interior pieces can
 * overlap freely with zero corner management, and the whole box fades with
 * plain opacity. The wordmark and rim render crisp above the blur.
 */
export function UnderwritingEngineBox({
  veilOpacity = 1,
  className,
  style,
}: UnderwritingEngineBoxProps) {
  return (
    <motion.span
      aria-hidden="true"
      className={cn("relative block", className)}
      style={{ ...style, opacity: veilOpacity }}
    >
      {/* The one shape. */}
      <span className="absolute inset-0 block" style={{ clipPath: HEX_CLIP }}>
        {/* Interior faces — oversized, clipped by the hexagon, blurred
            together into the frosted-cube glow. */}
        <span
          className="absolute block"
          style={{
            inset: -30,
            filter: "blur(14px)",
            // Full-bleed opaque base: the wrapper overshoots the clip by
            // 30px, so blur erosion happens OUTSIDE the hexagon and the
            // stack behind can never bleed through the interior.
            background:
              "linear-gradient(to bottom, #2E7BD8, #3D50E2 42%, #A44BDC 58%, #F07BC8 74%, #F8DFDC)",
          }}
        >
          {/* Top face (lid): hazy blue → cyan, Figma-sampled. 135deg in
              square space renders screen left→right. */}
          <span
            className="absolute"
            style={{ top: 24, left: 24, width: 352, height: 188 }}
          >
            <span
              className="absolute inset-0 m-auto"
              style={{
                ...DIAMOND_SHELL,
                borderRadius: 0,
                transform: "scaleY(0.5) rotate(-45deg) scale(1.12)",
                background:
                  "linear-gradient(135deg, #95BCDA, #1577DB 45%, #16A1DA)",
              }}
            />
          </span>
          {/* Left wall: deep blue joint → violet → pink → milky. */}
          <span
            className="absolute"
            style={{
              top: 112,
              left: 20,
              width: 190,
              height: 150,
              transform: "skewY(26.57deg)",
              transformOrigin: "0 0",
              background:
                "linear-gradient(to bottom, #0F69D5, #8C52E7 32%, #F581DD 58%, #F7DEDC 85%, #FAF0EE)",
            }}
          />
          {/* Right wall: mirrored wash. */}
          <span
            className="absolute"
            style={{
              top: 112,
              left: 190,
              width: 190,
              height: 150,
              transform: "skewY(-26.57deg)",
              transformOrigin: "100% 0",
              background:
                "linear-gradient(to bottom, #4594D5, #E366CA 32%, #F48EA5 58%, #F8DFDC 85%, #FAF0EE)",
            }}
          />
          {/* Front edge: the bright cyan→magenta→salmon seam. */}
          <span
            className="absolute"
            style={{
              top: 190,
              left: 194,
              width: 12,
              height: 130,
              background:
                "linear-gradient(to bottom, #3BC8FE, #BB3DE0 45%, #F0789A 80%, #F8DFDC)",
            }}
          />
        </span>

        {/* Lid — a near-crisp top face over the blur; its visible lower
            edges + shadow are what make the shape read as a CUBE instead of
            a flat hexagon (Figma 7617:32860). Slightly scaled down so the
            blurred halo shows around it as the glass rim/thickness. */}
        <span
          className="absolute"
          style={{
            top: 0,
            left: 0,
            width: 340,
            height: 176,
            filter: "blur(2px) drop-shadow(0 12px 26px rgba(23, 42, 190, 0.5))",
          }}
        >
          <span
            className="absolute inset-0 m-auto"
            style={{
              ...DIAMOND_SHELL,
              transform: "scaleY(0.5) rotate(-45deg) scale(0.97)",
              // 225deg in square space renders screen top→bottom: cyan at
              // the far vertex, deep blue at the near (front) vertex.
              background:
                "linear-gradient(225deg, #55CBF2, #2E7FE2 45%, #1D3FE3)",
            }}
          >
            {/* Lid rim — the square's left+bottom borders render as the
                diamond's two lower edges, the lid↔wall junction. Dark
                crease, per the Figma. */}
            <span
              className="absolute inset-0"
              style={{
                borderRadius: "inherit",
                borderLeft: "2px solid rgba(13, 25, 110, 0.4)",
                borderBottom: "2px solid rgba(13, 25, 110, 0.4)",
              }}
            />
          </span>
        </span>

        {/* Front-corner seam — the vertical edge where the two walls meet,
            running from the lid's near vertex to the floor vertex. */}
        <span
          className="absolute"
          style={{
            left: 169,
            top: 170,
            width: 2.5,
            height: 116,
            background:
              "linear-gradient(to bottom, rgba(13,25,110,0.45), rgba(140,40,120,0.3) 55%, rgba(180,80,120,0.12))",
            filter: "blur(0.5px)",
          }}
        />

        {/* Wordmark — crisp, centred on the top face. */}
        <span
          className="absolute"
          style={{
            width: 78,
            left: 170,
            top: 85,
            transform: `translate(-50%,-50%) ${TEXT_PROJECTION}`,
            transformOrigin: "center",
          }}
        >
          <EngineWordmark />
        </span>
      </span>

      {/* Rim — crisp silhouette stroke above the blurred interior. */}
      <svg
        viewBox="0 0 340 286"
        className="absolute inset-0 block h-full w-full"
        style={{ overflow: "visible" }}
      >
        <path
          d={HEX_OUTLINE}
          fill="none"
          stroke="rgba(30, 30, 90, 0.3)"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
      </svg>
    </motion.span>
  );
}

// Wayflyer wordmark (Figma node 7617:32857) — the only inline vector kept.
// White over the dark ramp, receding over lighter frost via `lighten`.
function EngineWordmark() {
  return (
    <svg
      viewBox="0 0 77.8894 16.9834"
      fill="none"
      style={{ display: "block", width: "100%", mixBlendMode: "lighten" }}
      aria-hidden
    >
      <path
        d="M77.0903 3.67049C76.4087 3.67049 75.7977 3.83572 75.2571 4.15438C74.7165 4.48484 74.317 4.96874 74.0702 5.62966H73.4121V3.77671H70.7797V6.3614H72.0607V13.2185H74.6107V8.45039C74.6107 7.74226 74.7988 7.21116 75.1748 6.8807C75.5509 6.53843 76.0797 6.3614 76.7613 6.3614H77.8894V3.67049H77.0903Z"
        fill="white"
      />
      <path
        d="M67.7832 4.15439C67.0781 3.77672 66.2437 3.58788 65.2566 3.58788C64.2695 3.58788 63.3764 3.78852 62.636 4.18979C61.9074 4.57927 61.3433 5.13397 60.9438 5.87751C60.5442 6.59745 60.3444 7.47081 60.3444 8.474C60.3444 9.47719 60.5442 10.3624 60.9555 11.1059C61.3668 11.8376 61.9427 12.4159 62.6948 12.8172C63.4469 13.2185 64.3282 13.4191 65.3389 13.4191C66.3495 13.4191 67.6069 13.1359 68.406 12.5694C69.2052 11.9911 69.7222 11.2003 69.922 10.1971H67.3249C67.1839 10.5866 66.9606 10.8699 66.6316 11.0587C66.3025 11.2475 65.8677 11.3419 65.3154 11.3419C64.7631 11.3419 64.0462 11.1531 63.6231 10.7872C63.2001 10.4214 62.9298 9.89027 62.824 9.18214H69.9573V8.29697C69.9573 7.31738 69.7692 6.47943 69.3932 5.78309C69.0171 5.07496 68.4765 4.54386 67.7597 4.16619L67.7832 4.15439ZM62.918 7.44721C63.0591 6.8571 63.3176 6.40861 63.7171 6.11356C64.1285 5.8067 64.6455 5.65327 65.2801 5.65327C65.9147 5.65327 66.42 5.8067 66.7961 6.11356C67.1721 6.40861 67.4072 6.8571 67.4894 7.44721H62.918Z"
        fill="white"
      />
      <path
        d="M40.7075 0.519298C40.3314 0.849761 40.1434 1.38086 40.1434 2.08899V3.77671H38.4394V5.8185H40.1199V13.2185H42.717V5.8185H44.797V3.77671H42.6818V2.04179H44.9851V0H42.2939C41.6124 0 41.0835 0.177034 40.7075 0.519298Z"
        fill="white"
      />
      <path
        d="M46.1601 0V11.1295C46.1601 11.8376 46.3482 12.3687 46.7242 12.6992C47.1003 13.0415 47.6291 13.2185 48.3107 13.2185H49.9089V11.1767H48.769V0H46.1601Z"
        fill="white"
      />
      <path
        d="M32.5402 10.1381H32.4814L29.8491 3.77672H27.0874L31.1417 13.1359L30.4014 14.8944H28.3331V16.9834H30.4836C31.1887 16.9834 31.7176 16.8064 32.0701 16.4641C32.4109 16.1337 32.7164 15.6498 32.975 15.036L33.7388 13.2067L37.7696 3.76491H35.102L32.5284 10.1263L32.5402 10.1381Z"
        fill="white"
      />
      <path
        d="M26.1003 7.28198C26.1003 6.07815 25.7242 5.15758 24.9839 4.53206C24.2553 3.90654 23.1859 3.58788 21.7875 3.58788C20.389 3.58788 19.5077 3.85933 18.7438 4.39043C17.9799 4.92153 17.4981 5.71228 17.2866 6.75088H19.872C20.0952 6.01914 20.7298 5.65327 21.7875 5.65327C22.8451 5.65327 23.609 6.16077 23.609 7.17576V7.54163H20.9178C19.7074 7.54163 18.7791 7.78947 18.1327 8.29697C17.4864 8.80446 17.1573 9.5244 17.1573 10.4568C17.1573 11.3892 17.4746 12.0501 18.0975 12.593C18.7203 13.1241 19.5899 13.3955 20.6946 13.3955C21.7992 13.3955 22.187 13.2539 22.7276 12.9588C23.2682 12.652 23.703 12.2271 24.032 11.6842H24.6901V13.2303H27.3225V10.6456H26.1003V7.28198ZM23.6207 9.40638C23.6207 9.78405 23.5149 10.1263 23.2917 10.4332C23.0684 10.7282 22.7746 10.9643 22.3985 11.1413C22.0225 11.3183 21.5877 11.4128 21.0941 11.4128C20.6006 11.4128 20.295 11.3183 20.0247 11.1295C19.7662 10.9407 19.6369 10.6692 19.6369 10.327C19.6369 9.61882 20.107 9.27655 21.0471 9.27655H23.6207V9.41818V9.40638Z"
        fill="white"
      />
      <path
        d="M12.0101 0V2.57289H14.3487V2.6437L12.6565 9.06411H12.5977L10.3414 2.04179H5.5585V4.62647H6.21659V4.70909L4.88866 9.06411H4.8064L3.40796 3.77671H0V6.3614H1.43369V6.43221L3.34921 13.2185H6.00507L8.63742 5.90111H8.69618L11.4578 13.2185H14.1019L17.8624 0H12.0101Z"
        fill="white"
      />
      <path
        d="M55.9493 6.3614H56.6074V6.44402L55.1149 10.1381H55.0562L52.4238 3.77672H49.6622L53.7165 13.1359L52.9762 14.8944H50.9079V16.9834H53.0584C53.7635 16.9834 54.2923 16.8064 54.6449 16.4641C54.9857 16.1337 55.2912 15.6498 55.5498 15.036L56.3136 13.2067L60.3444 3.76491H55.9493V6.3614Z"
        fill="white"
      />
    </svg>
  );
}
